/// <amd-module name="typeahead/core/TypeaheadUtils"/>

/// <amd-dependency path="underscore" name="_" />
/// <reference path="../../typings/thirdparty/modernizr.d.ts" />
/// <amd-dependency path="typeahead/core/loc" name="loc"/>
/// <amd-dependency path="typeahead/core/context" name="coreContext"/>

/// <reference path="../../typings/typeahead/loc.d.ts" />
/// <reference path="../../typings/typeahead/window.d.ts" />

import {ITrackingData} from "typeahead/core/logging/OmnitureLogger";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";

export enum FlightType {
    OneWay, RoundTrip, MultiDestination, Unknown,
}

// This enum has sample LOB values to be logged into omniture reflecting the LOB grouping in any wizard
export enum Lob {
    Flight, Hotel, Package, Car, Cruise, Activity, Rail, VacationRental, OpenSearch, Unknown,
}

export enum PackageType {
    FlightHotel, FlightHotelCar, FlightCar, HotelCar, Unknown,
}

export enum RailType {
    OneWay, RoundTrip, Unknown,
}

export enum SiteParam {
    eapid, tpid,
}

export const MercuryWizardLandingPagesPackageTypes: string[] = [
    "packageFlightHotel", "flightHotel",
    "packageFlightHotelCar", "flightHotelCar", "carHotelFlight",
    "packageFlightCar", "flightCar", "carFlight",
    "packageHotelCar", "hotelCar", "carHotel",
];

export class TypeaheadUtils {

    public static responsiveTAClasses = {
        autoCompleteClass : "autocomplete",
        centerClass : "pin-center",
        dropdownClass : "autocomplete-dropdown",
        hlClass : "highlight",
        leftClass : "pin-left",
        openClass : "autocomplete-open",
        rightClass : "pin-right",
    };

    public static bitMaskFlags = {
        ADDRESS : 32,
        AIRPORT : 1,
        CITY : 2,
        HOTEL : 128,
        METROCODE : 64,
        METROSTATION : 1024,
        MULTICITY : 4,
        MULTIREGION : 256,
        NEIGHBORHOOD : 8,
        POI : 16,
        TRAINSTATION : 512,
    };

    public static defaultMask: number = (
        /* tslint:disable-next-line: no-bitwise */
        TypeaheadUtils.bitMaskFlags.AIRPORT |
        TypeaheadUtils.bitMaskFlags.CITY |
        TypeaheadUtils.bitMaskFlags.MULTICITY |
        TypeaheadUtils.bitMaskFlags.NEIGHBORHOOD |
        TypeaheadUtils.bitMaskFlags.POI
    );

    public static LOB_ATTRIBUTE = {
        ACTIVITIES: "ACTIVITIES",
        CARS: "CARS",
        FLIGHTS : "FLIGHTS",
        HOTELS : "HOTELS",
        PACKAGES : "PACKAGES",
        RAILS: "RAILS",
        TEST: "",
        VACATIONRENTALS: "VACATIONRENTALS",
    };

    public static CATEGORIES = {
        ADDRESS: {
            icon: "location",
            id: 4,
            name: loc.taCategoryNameAddress,
        },
        AIRPORT: {
            icon: "flights",
            id: 2,
            name: loc.taCategoryNameAirports,
        },
        AMENITY: {
            icon: "tag",
            id: 14,
            name: loc.taCategoryNameAmenities,
        },
        ATTRACTION: {
            icon: "location",
            id: 1,
            name: loc.taCategoryNameAttractions,
        },
        BESTFILTER: {
            icon: "search",
            id: 17,
        },
        CITY: {
            icon: "location",
            id: 0,
            name: loc.taCategoryNameRegionCity,
        },
        DEALS: {
            icon: "location",
            id: 13,
            name: loc.taCategoryNameDeals,
        },
        GEOLOCATION: {
            icon: "geolocation",
            id: 16,
            name: undefined,
        },
        HOTEL: {
            icon: "hotels",
            id: 3,
            name: loc.taCategoryNameHotels,
        },
        METROCODE: {
            icon: "flights",
            id: 9,
            name: loc.taCategoryNameAirports,
        },
        METROSTATION: {
            icon: "train",
            id: 6,
            name: loc.taCategoryNameMetros,
        },
        MULTICITY: {
            icon: "location",
            id: 7,
            name: loc.taCategoryNameRegionCity,
        },
        NEIGHBORHOOD: {
            icon: "location",
            id: 8,
            name: loc.taCategoryNameRegionCity,
        },
        POI: {
            icon: "location",
            id: 11,
            name: loc.taCategoryNameAttractions,
        },
        RENTAL: {
            icon: "cars",
            id: 12,
            name: loc.taCategoryNameRentals,
        },
        TRAINSTATION: {
            icon: "train",
            id: 5,
            name: loc.taCategoryNameTrains,
        },
        USERHISTORY: {
            icon: "notime",
            id: 15,
            name: loc.taCategoryNameRecentSearch,
        },
    };

    public static getCookie(name): string {
        let value = "; " + document.cookie;
        let parts = value.split("; " + name + "=");
        if (parts.length === 2) {
            let cookieVal = parts.pop().split(";").shift();
            return cookieVal;
        }
        return "";
    }

    public static getAbovMapFromCookie(abovCookieValue): Object {
        let abovMap: Object = {};
        if (typeof abovCookieValue === "string") {
            let experiments = abovCookieValue.split(":");
            experiments.forEach((experiment): void => {
                let expTokens = experiment.split("|");
                if (expTokens.length === 3 && expTokens[0].length > 0 && expTokens[2].length > 0) {
                    abovMap[expTokens[0]] = expTokens[2];
                }
            });
        }

        return abovMap;
    }

    public static getGuid(): string {
        let guid = window.dctk && window.dctk.logging ? window.dctk.logging.guid : undefined;
        if (guid === undefined) {
            guid = window.utag_data ? window.utag_data.guid : undefined;
        }
        if (guid === undefined) {
            try {
                require(["gc/pageModel"], (gcPageModel) => {
                    if (gcPageModel && gcPageModel.user) {
                        guid = gcPageModel.user.guid;
                    }
                });
            } catch (e) {
                // Ignore
            }
        }
        if (guid === undefined) {
            let mc1Cookie = this.getCookie("MC1");
            let pair = mc1Cookie && mc1Cookie.split("=");
            guid = $.isArray(pair) && pair.length === 2 ? pair[1] : "";
        }
        if (guid === undefined) {
            TypeaheadErrorLogger.log(
                new Error("ERROR: Unable to get 'guid'"), Severity.MustFix, ProblemOrigin.ClientOfUniversalTypeahead);
        } else {
            guid = guid.replace(/-/g, "");
        }
        return guid;
    }

    // Get value of tpid or eapid
    public static getSiteParamValue(siteParam: SiteParam): number {
        let paramValue: number = undefined;
        const siteParamCookieConfig = {
            eapid: {
                cookieName: "iEAPID",
            },
            tpid: {
                cookieName: "tpid",
            },
        };
        const param: string = SiteParam[siteParam];
        if (window.utag_data && window.utag_data.context && window.utag_data.context.site
            && window.utag_data.context.site[param] !== undefined) {
            paramValue = window.utag_data.context.site[param];
        } else {
            try {
                require(["gc/pageModel"], (gcPageModel) => {
                    if (gcPageModel && gcPageModel.site && gcPageModel.site[param] !== undefined) {
                        paramValue = gcPageModel.site[param];
                    }
                });
            } catch (e) {
                // Ignore
            }
        }
        if (paramValue === undefined) {
            // Sample tpid cookie value - v.1,3
            // Sample iEAPID cookie value - 0
            let cookie: string = this.getCookie(siteParamCookieConfig[param].cookieName);
            if (cookie !== undefined) {
                let cookieTokens: string[] = cookie.split(",");
                paramValue = parseInt(cookieTokens.length > 1 ? cookieTokens[1] : cookieTokens[0], 10);
            }
        }
        if (typeof paramValue !== "number" || isNaN(paramValue)) {
            TypeaheadErrorLogger.log(new Error("Unable to get " + param + " from utag_data/cookies/gcPageModel"),
                Severity.Excusable, ProblemOrigin.ClientOfUniversalTypeahead);
        }
        return paramValue;
    }

    public static getPointOfSale(): string {
        return window.location.hostname;
    }

    public static getLocale(): string {
        return coreContext.locale;
    }

    public static getSiteId(): string {
        // Below type coversion to string is a hotfix.
        // coreContext.siteid should be ideally set as string in the typeahead java layer
        return coreContext.siteid + "";
    }

    // Gets page ID from a dom element on the page
    public static getPageId(): string {
        let pageId = $("#pageId").val();
        if (!pageId) {
            pageId = "";
        }
        return pageId;
    }

    public static getDeviceType(): string {
        if (!TypeaheadUtils.deviceType) {
            TypeaheadUtils.deviceType = window.utag_data ? window.utag_data.device_type :
                TypeaheadUtils.evaluateDeviceType();
        }
        return TypeaheadUtils.deviceType;
    }

    public static getBrowser(): string {
        let browser = (window.utag_data && window.utag_data.context && window.utag_data.context.clientInfo &&
            window.utag_data.context.clientInfo.deviceCharacteristics) ?
            window.utag_data.context.clientInfo.deviceCharacteristics.mobileBrowser : undefined;

        if (!browser) {
            browser = TypeaheadUtils.evaluateBrowserName();
        }
        return browser;
    }

    // Gets the client ID from the input element / meta DOM element / url PATH
    public static getClientId(): string {
        let clientId: string = $("input[data-provide=utypeahead]").data("clientId");
        if (!clientId) {
            clientId = $("meta[name=essClientId]").attr("content");
        }
        if (!clientId) {
            let pageId: string = TypeaheadUtils.defaultPageIdGetter();
            if (pageId) {
                clientId = TypeaheadUtils.clientIdByPageId[pageId];
            }
        }
        if (!clientId) {
            clientId = TypeaheadUtils.clientIdByPath[window.location.pathname];
        }
        if (!clientId) {
            TypeaheadErrorLogger.log(new Error("Could not determine client id"), Severity.Excusable,
                ProblemOrigin.UniversalTypeahead);
            clientId = TypeaheadUtils.TEST_CLIENT_ID;
        }
        return clientId ? clientId : "Unknown";
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setClientId(clientId: string) {
        TypeaheadUtils.TEST_CLIENT_ID = clientId;
    }

    public static isStorefrontClient(): boolean {
        let storefrontClientIds: string[] = [
            "Homepage",
            "AWSHomepage",
            "LXLandingPage",
            "CarsLandingPage",
            "FlightsLandingPage",
            "HotelsLandingPage",
            "PackageLandingPage",
            "RailsLandingPage"];
        return storefrontClientIds.indexOf(TypeaheadUtils.getClientId()) > -1;
    }

    public static getLob(): Lob {
        if ("OpenSearchPage" === TypeaheadUtils.getClientId()) {
            return Lob.OpenSearch;
        }
        // Search if wizard page has LOB
        let lob: string = $("div.wizard-tabs").find("button.on").data("lob");
        // If page does not have LOB search if uTA input has data-lob
        lob = lob ? lob : $("input[data-provide=utypeahead]:visible").data("lob");
        // Check if the TEST LOB attribute is set
        lob = lob ? lob : TypeaheadUtils.LOB_ATTRIBUTE.TEST;

        if (typeof lob === "string") {
            lob = lob.toLowerCase();
            if (lob.indexOf("pack") > -1 ||
                MercuryWizardLandingPagesPackageTypes.map((type) => {return type.toLowerCase(); }).indexOf(lob) > -1) {
                return Lob.Package;
            } else if (lob.indexOf("flight") > -1) {
                return Lob.Flight;
            } else if (lob.indexOf("hotel") > -1) {
                return Lob.Hotel;
            } else if (lob.indexOf("vacationrental") > -1) {
                return Lob.VacationRental;
            } else if (lob.indexOf("car") > -1) {
                return Lob.Car;
            } else if (lob.indexOf("cru") > -1) {
                return Lob.Cruise;
            } else if (lob.indexOf("lx") > -1 || lob.indexOf("activit") > -1) {
                return Lob.Activity;
            } else if (lob.indexOf("train") > -1 || lob.indexOf("rail") > -1) {
                return Lob.Rail;
            } else if (lob.indexOf("open") > -1) {
                return Lob.OpenSearch;
            }
        }
        return Lob.Unknown;
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setLob(lob: string) {
        TypeaheadUtils.LOB_ATTRIBUTE.TEST = lob;
    }

    public static getFlightType(lob: Lob): FlightType {
        if (lob === Lob.Flight) {
            let flightTypeElementId =
                $("input[name=route-type], input[name=flight-type]").filter(":checked:visible").attr("id");
            flightTypeElementId = flightTypeElementId ? flightTypeElementId : TypeaheadUtils.TEST_FLIGHT_TYPE_ID;
            if (flightTypeElementId) {
                flightTypeElementId = flightTypeElementId.toLowerCase();
                if (flightTypeElementId.indexOf("one") > -1) {
                    return FlightType.OneWay;
                } else if (flightTypeElementId.indexOf("round") > -1) {
                    return FlightType.RoundTrip;
                } else if (flightTypeElementId.indexOf("multi") > -1) {
                    return FlightType.MultiDestination;
                } else {
                    TypeaheadErrorLogger.log(new Error("Could not determine flight type with dom element id - " +
                            flightTypeElementId),
                        Severity.Excusable,  ProblemOrigin.ClientOfUniversalTypeahead);
                }
            }
        }
        return FlightType.Unknown;
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setFlightElementId(id: string) {
        TypeaheadUtils.TEST_FLIGHT_TYPE_ID = id;
    }

    public static getRailType(lob: Lob): RailType {
        if (lob === Lob.Rail) {
            let railTypeElementId =
                $("input[name=journeyType]").filter(":checked").attr("id");
            railTypeElementId = railTypeElementId ? railTypeElementId : TypeaheadUtils.TEST_RAIL_TYPE_ID;
            if (railTypeElementId) {
                railTypeElementId = railTypeElementId.toLowerCase();
                if (railTypeElementId.indexOf("one") > -1) {
                    return RailType.OneWay;
                } else if (railTypeElementId.indexOf("round") > -1) {
                    return RailType.RoundTrip;
                } else {
                    TypeaheadErrorLogger.log(new Error("Could not determine rail journey type with dom element id - " +
                            railTypeElementId),
                        Severity.MustFix,  ProblemOrigin.ClientOfUniversalTypeahead);
                }
            }
            TypeaheadErrorLogger.log(new Error("Unable to find journeyType dom element to determine rail journey type"),
                Severity.MustFix,  ProblemOrigin.ClientOfUniversalTypeahead);
        }
        return RailType.Unknown;
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setRailTypeId(id: string) {
        TypeaheadUtils.TEST_RAIL_TYPE_ID = id;
    }

    public static getPackageType(): PackageType {
        // Homepage
        let packageTypeElementValue = $("input[name=package-lob], input[name=package-type]")
            .filter(":visible:checked").attr("value");
        packageTypeElementValue = (packageTypeElementValue || TypeaheadUtils.TEST_PACKAGE_TYPE_VALUE);
        if (packageTypeElementValue) {
            packageTypeElementValue = packageTypeElementValue.toLowerCase();
            if (packageTypeElementValue.indexOf("flight-hotel-car") > -1) {
                return PackageType.FlightHotelCar;
            } else if (packageTypeElementValue.indexOf("flight-hotel") > -1) {
                return PackageType.FlightHotel;
            } else if (packageTypeElementValue.indexOf("flight-car") > -1) {
                return PackageType.FlightCar;
            } else if (packageTypeElementValue.indexOf("hotel-car") > -1) {
                return PackageType.HotelCar;
            }
        }
        // Landing Pages
        let packageType = $("div.wizard-tabs").find("button.on").data("lob");
        if (packageType) {
            switch (packageType) {
                case "packageFlightHotel":
                case "flightHotel":
                    return PackageType.FlightHotel;
                case "packageFlightHotelCar":
                case "flightHotelCar":
                case "carHotelFlight":
                    return PackageType.FlightHotelCar;
                case "packageFlightCar":
                case "flightCar":
                case "carFlight":
                    return PackageType.FlightCar;
                case "packageHotelCar":
                case "hotelCar":
                case "carHotel":
                    return PackageType.HotelCar;
                default:
                    return PackageType.Unknown;
            }
        }
        return PackageType.Unknown;
    }

    public static getShortPackageType(): string {
        let packageType: PackageType = TypeaheadUtils.getPackageType();
        switch (packageType) {
            case PackageType.FlightCar: return "FC";
            case PackageType.FlightHotel: return "FH";
            case PackageType.FlightHotelCar: return "FHC";
            case PackageType.HotelCar: return "HC";
            default:
                return undefined;
        }
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setPackageTypeValue(value: string) {
        TypeaheadUtils.TEST_PACKAGE_TYPE_VALUE = value;
    }

    public static submitToOmniture (trackingData: ITrackingData, activeESSTests: string): void {
        require(["dctk/dctk"], (dctk) => {
            if (activeESSTests) {
                dctk.addExperiments(activeESSTests);
            }
            dctk.trackEvent("o", "wizard-submit", trackingData, trackingData.eventString);
        });
    }

    public static isWithinMobileBreakpoint(): boolean {
        return (uitk && uitk.mediaquery &&
        uitk.mediaquery.getMediaQueries() &&
        uitk.mediaquery.getMediaQueries().mobile &&
        uitk.mediaquery.getMediaQueries().mobile.matches);
    }

    public static isTouchDevice(): boolean {
        return Modernizr && Modernizr.touch;
    }

    public static getExpUserId(): string {
        let expuserid: string = $("#scratchpad-badge-expuserid").attr("data-id");
        if (!expuserid) {
            require(["gc/pageModel"], (gcPageModel) => {
                expuserid = gcPageModel.user ? gcPageModel.user.expUserId : undefined;
            });
        }
        return expuserid || "-1";
    }

    public static iconOverride (category: any, icon: string): any {
        // creates a copy of the category in order to not override the icon
        // in the original category entry, and returns the copy with icon replaced
        let copy = { ...category};
        copy.icon = icon;
        return copy;
    }

    public static getOverrideIcon (type: string, subtype?: string): any {
        if (typeof subtype === "string") {
            type = type + "|" + subtype;
        }
        return TypeaheadUtils.categoryIconOverrides[type];
    }

    private static categoryIconOverrides = {
        "ADDRESS|RENTALLOCATION" : TypeaheadUtils.CATEGORIES.RENTAL.icon,
        "NEIGHBORHOOD|AIRNBH": "flights",
    };

    private static TEST_CLIENT_ID = "Unknown";
    private static TEST_FLIGHT_TYPE_ID = "Unknown";
    private static TEST_PACKAGE_TYPE_VALUE = "Unknown";
    private static TEST_RAIL_TYPE_ID = "Unknown";

    private static clientSideExperimentVariants: string = "";

    private static clientIdByPageId = {
        "Homepage": "Homepage",
        "aws_Homepage": "AWSHomepage",
        "page.Account.MyAccount": "UserAccountPage",
        "page.Activities": "LXLandingPage",
        "page.Cars": "CarsLandingPage",
        "page.Cars.Infosite.Information": "Cars.Search",
        "page.Flight-Search-Oneway": "Flights.Search",
        "page.Flight-Search-Roundtrip.Out": "Flights.Search",
        "page.Flight-Search.MDest": "Flights.Search",
        "page.Flights": "FlightsLandingPage",
        "page.Hotel-Search": "Hotels.Search",
        "page.Hotels": "HotelsLandingPage",
        "page.Hotels.Search": "Hotels.Search",
        "page.Packages": "PackageLandingPage",
        "page.Rails": "RailsLandingPage",
        "page.car.search.list.aws": "Cars.Search",
        "page.open-search": "OpenSearchPage",
    };

    private static clientIdByPageName = {
        activity: "LXLandingPage",
        aws_Homepage: "AWSHomepage",
        car: "CarsLandingPage",
        flight: "FlightsLandingPage",
        homepage: "Homepage",
        hotel: "HotelsLandingPage",
        package: "PackageLandingPage",
    };

    private static clientIdByPath = {
        "/": "Homepage",
        "/Activities" : "LXLandingPage",
        "/Cars" : "CarsLandingPage",
        "/Flights": "FlightsLandingPage",
        "/Flights-Search": "Flights.Search",
        "/Holidays": "PackageLandingPage",
        "/Hotel-Search": "Hotels.Search",
        "/Hotels": "HotelsLandingPage",
        "/Vacation-Packages": "PackageLandingPage",
        "/search-results": "OpenSearchPage",
        "/trains": "RailsLandingPage",
    };

    private static deviceType;

    /* Device type evaluation code referred from -
     *   https://ewegithub.sb.karmalab.net/EWE/tealium/blob/master/src/main/extension/device-characteristics.js
     */
    private static evaluateDeviceType(): string {
        if (/iPad|Transformer|ME173X|KOOF|Nook|IMM76D|Nexus 10|Nexus 7|Tablet/i.test(navigator.userAgent) ||
            /HP Slate|PG09410|PG41200|Mediapad|Kindle|Tab|Xoom|TegraNote|SCH-I800/i.test(navigator.userAgent)) {
            return "Tablet";
        } else if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            return "Mobile";
        } else {
            return "Desktop";
        }
    }

    private static evaluateBrowserName(): string {
        let ua = navigator.userAgent;
        let tem;
        let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return "IE";
        }
        if (M[1] === "Chrome") {
            tem = ua.match(/\bOPR\/(\d+)/);
            if (tem != null) {
                return "Opera";
            }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
        tem = ua.match(/version\/(\d+)/i);
        if (tem != null) {
            M.splice(1, 1, tem[1]);
        }
        return M[0];
    }

    private static defaultPageIdGetter(): string {
        if (window.xPageId) {
            return window.xPageId;
        }
        let pageIdElement: any = $("#pageId");
        let pageId = pageIdElement.length === 1 ? pageIdElement.val() : "";
        if (!pageId && window.pageName) {
            pageId = TypeaheadUtils.clientIdByPageName[window.pageName];
        }
        if (!pageId) {
            pageId = window.dctk ? (window.dctk.logging ? window.dctk.logging.pageName : "") : "";
        }
        return pageId ? pageId : "";
    }
}
